import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, ArrowRight } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ServiceTestimonials from '../../components/ServiceTestimonials';
import ContactFormDialog from '../../components/ContactFormDialog';
import { Button } from '../../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';

const CompanyFormation = () => {
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const pricingPlans = [
    { name: "Starter", price: "$149", features: ["LLC Formation", "Articles of Organization", "Registered Agent (1st Year)", "Operating Agreement Template", "EIN Registration"] },
    { name: "Business", price: "$299", popular: true, features: ["Everything in Starter", "Trademark Registration", "Business License Research", "Priority Processing", "Compliance Calendar", "Annual Report Filing"] },
    { name: "Enterprise", price: "$499", features: ["Everything in Business", "Corporation Option", "Custom Operating Agreement", "Tax Strategy Consultation", "Multi-state Registration", "Dedicated Business Advisor"] }
  ];

  const process = [
    { step: "01", title: "Choose Your Entity", description: "Select the right business structure for your goals. We help you decide between LLC, Corporation, or other entity types." },
    { step: "02", title: "Document Preparation", description: "We prepare all required formation documents, articles of organization, and operating agreements for your state." },
    { step: "03", title: "Filing & Registration", description: "We file with the state, obtain your EIN, and deliver your complete business formation package ready to operate." }
  ];

  const testimonials = [
    { name: "Amanda Stevens", role: "Small Business Owner", text: "LLC formation and trademark registration handled together. The combined package saved me time and money." },
    { name: "James Anderson", role: "Tech Startup CEO", text: "Company formation was completed faster than expected. Perfect coordination with trademark filing." },
    { name: "Rachel Green", role: "E-commerce Seller", text: "Started my LLC with ease. The team guided me through every step of the business registration process." },
    { name: "Mark Davis", role: "Consultant", text: "Professional LLC formation service. All paperwork was handled correctly and delivered on time." },
    { name: "Sofia Patel", role: "Freelance Designer", text: "Formed my LLC to protect personal assets. The process was straightforward and well-explained." }
  ];

  const faqs = [
    { question: "What's the difference between an LLC and a Corporation?", answer: "An LLC offers flexible management and pass-through taxation with liability protection. A Corporation has a more formal structure with shareholders and directors, and may be better for raising investment capital." },
    { question: "How long does LLC formation take?", answer: "Processing times vary by state, but most LLC formations are completed within 1-2 weeks. Expedited options are available for faster processing." },
    { question: "Do I need an LLC before registering a trademark?", answer: "No, you can file a trademark as an individual. However, forming an LLC first provides liability protection and aligns your business structure with your brand protection strategy." },
    { question: "What state should I form my LLC in?", answer: "Most small businesses should form in their home state. We can advise on multi-state options like Delaware or Wyoming for specific business advantages." }
  ];

  return (
    <div className="min-h-screen bg-wine-950">
      <Header onOpenContactForm={() => setIsContactFormOpen(true)} />
      
      <section className="pt-24 pb-16 bg-gradient-to-b from-wine-900/50 to-wine-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/" className="inline-flex items-center text-gold-400 hover:text-gold-500 mb-6 text-sm font-medium transition-colors"><ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Home</Link>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-4">Company Formation & LLC Services</h1>
          <p className="text-lg text-wine-300 max-w-2xl mx-auto">Launch your business the right way with professional LLC formation and company registration services.</p>
        </div>
      </section>

      <section className="py-20 bg-cream-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="rounded-2xl overflow-hidden h-80 shadow-lg">
              <img src="https://images.unsplash.com/photo-1770992161088-7ad66282c9af?w=800&h=600&fit=crop" alt="Modern corporate office interior" className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-wine-500 mb-3">About This Service</p>
              <h2 className="text-3xl font-bold text-wine-950 mb-4">Start Your Business on Solid Ground</h2>
              <p className="text-stone-600 leading-relaxed mb-4">Company formation establishes your business as a legal entity, providing liability protection and professional credibility. We handle LLC formation, corporation setup, and business registration across all 50 states.</p>
              <p className="text-stone-600 leading-relaxed mb-6">Bundle with trademark registration to protect both your business structure and brand identity from day one. Our team coordinates both filings for a seamless launch.</p>
              <Button onClick={() => setIsContactFormOpen(true)} className="bg-wine-600 hover:bg-wine-700 text-white transition-colors">Start My LLC</Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-wine-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Our 3-Step Process</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {process.map((item) => (
              <div key={item.step} className="bg-wine-950 p-8 rounded-xl border border-wine-800/40 hover:border-gold-500/30 hover:-translate-y-1 hover:shadow-xl transition-all duration-300">
                <div className="w-10 h-10 bg-wine-600 rounded-lg flex items-center justify-center text-white font-bold text-sm mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-sm text-wine-400 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-wine-950 text-center mb-12">Formation Packages</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pricingPlans.map((plan) => (
              <div key={plan.name} className={`relative bg-white p-8 rounded-xl border-2 ${plan.popular ? 'border-wine-600 shadow-xl shadow-wine-600/10' : 'border-stone-200'} hover:-translate-y-1 hover:shadow-xl hover:border-gold-400 transition-all duration-300`}>
                {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><span className="bg-wine-600 text-white px-3 py-1 rounded-full text-xs font-bold">MOST POPULAR</span></div>}
                <h3 className="text-xl font-bold text-wine-950 mb-2">{plan.name}</h3>
                <div className="text-4xl font-bold text-wine-950 mb-6">{plan.price}</div>
                <ul className="space-y-3 mb-8">{plan.features.map((f, i) => (<li key={i} className="flex items-start"><Check className="w-4 h-4 text-wine-600 mr-2 mt-0.5 flex-shrink-0" /><span className="text-sm text-stone-600">{f}</span></li>))}</ul>
                <Button onClick={() => setIsContactFormOpen(true)} className={`w-full ${plan.popular ? 'bg-wine-600 hover:bg-wine-700 text-white' : 'bg-stone-100 hover:bg-gold-500 hover:text-wine-950 text-stone-800'} transition-colors`}>Choose {plan.name}</Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-wine-900 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-400 mb-3">Get Started</p>
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Launch Your Business?</h2>
          <p className="text-wine-200 mb-8">Professional company formation with optional trademark registration bundling.</p>
          <Button onClick={() => setIsContactFormOpen(true)} className="bg-gold-500 hover:bg-gold-400 text-wine-950 font-semibold px-8 py-5 text-base transition-colors">Start Formation <ArrowRight className="ml-2 w-4 h-4" /></Button>
        </div>
      </section>

      <ServiceTestimonials testimonials={testimonials} serviceName="Company Formation" />

      <section className="py-20" style={{ background: 'linear-gradient(180deg, #1A0A0E 0%, #0D0507 100%)' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-400 mb-3 text-center">FAQ</p>
          <h2 className="text-3xl font-bold text-white text-center mb-10">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-wine-900/50 rounded-xl px-6 border border-wine-800/40 hover:border-gold-400/30 transition-colors data-[state=open]:border-gold-400/40">
                <AccordionTrigger className="text-left text-base font-medium text-wine-100 hover:text-gold-400 py-5 [&[data-state=open]]:text-gold-400">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-wine-300 text-sm leading-relaxed pb-5">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <Footer onOpenContactForm={() => setIsContactFormOpen(true)} />
      <ContactFormDialog isOpen={isContactFormOpen} onClose={() => setIsContactFormOpen(false)} />
    </div>
  );
};

export default CompanyFormation;
